import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { applySelectedTenant } from '@saas-kit/lib/bootstrap';
import { useAuth, type TenantSummary } from '@saas-kit/lib/auth';

import { CheckIcon, ChevronDownIcon } from './icons';

/**
 * Top-nav workspace switcher. Renders nothing unless the user belongs to more
 * than one tenant — a single-workspace user already sees its name as the
 * sidebar brand, so there is nothing to switch.
 */
export function TenantSwitcher(): JSX.Element | null {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const tenants = useAuth((s) => s.tenants);
  const selectedTenant = useAuth((s) => s.selectedTenant);

  if (tenants.length < 2) return null;

  const onPick = (tenant: TenantSummary): void => {
    if (tenant.tenant_id === selectedTenant?.tenant_id) return;
    // Re-fetch tenant-scoped permissions, then reset to the dashboard so we
    // never linger on a page the new workspace's role can't access.
    void applySelectedTenant(tenant).then(() => navigate('/', { replace: true }));
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('workspace.switch')}
          data-testid="tenant-switcher"
          className="inline-flex h-9 max-w-[14rem] items-center gap-1.5 rounded-md border border-border px-2.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          <span className="truncate">
            {selectedTenant?.name ?? t('workspace.select')}
          </span>
          <ChevronDownIcon size={14} className="opacity-60" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 max-h-[20rem] min-w-[14rem] overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <DropdownMenu.Label className="px-2 py-1 text-xs text-muted-foreground">
            {t('workspace.label')}
          </DropdownMenu.Label>
          {tenants.map((tenant) => (
            <DropdownMenu.Item
              key={tenant.tenant_id}
              onSelect={() => onPick(tenant)}
              className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm outline-none focus:bg-muted"
            >
              <span className="truncate">{tenant.name}</span>
              {tenant.tenant_id === selectedTenant?.tenant_id ? (
                <CheckIcon size={14} className="shrink-0 text-primary" />
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
